import React from 'react';
import { ArrowLeft, ArrowRight, Settings, CreditCard, Shield, ShieldCheck, CheckCircle, XCircle, RotateCcw, Repeat, Monitor, Key, Globe, Sparkles, Wallet } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import SandboxCredentialsCta from '../components/SandboxCredentialsCta';

const bgImage = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/nlaucokx_WOMAN%20TECH.png';

const topics = [
  {
    icon: Settings,
    title: 'API Overview',
    description: 'Essential information for beginning your API integration. Covers authentication methods, base URLs, request formats, and core concepts you\'ll need before implementing specific functions.',
    link: '/technical-documentation/api-reference/overview',
  },
  {
    icon: CreditCard,
    title: 'Sale & Pre-Auth',
    description: 'Core payment processing functions for immediate sales and pre-authorisation requests. Learn how to capture payments instantly or hold funds for later completion.',
    link: '/technical-documentation/api-reference/sale-pre-auth',
  },
  {
    icon: Shield,
    title: '3D Secure Auth',
    description: 'Implement 3D Secure authentication to reduce fraud risk and meet regulatory requirements. Covers the initial authentication request and customer verification process.',
    link: '/technical-documentation/api-reference/3d-secure-auth',
  },
  {
    icon: ShieldCheck,
    title: '3D Secure Confirm',
    description: 'Finalise 3D Secure transactions after customer authentication. Learn how to confirm payments once the verification process is complete.',
    link: '/technical-documentation/api-reference/3d-secure-confirm',
  },
  {
    icon: CheckCircle,
    title: 'Complete Pre-Auth',
    description: 'Convert pre-authorised transactions into completed sales. Essential for businesses that need to confirm orders before charging customers.',
    link: '/technical-documentation/api-reference/complete-pre-auth',
  },
  {
    icon: XCircle,
    title: 'Void Pre-Auth',
    description: 'Release pre-authorised funds without capturing payment. Perfect for handling cancelled orders or inventory shortages.',
    link: '/technical-documentation/api-reference/void-pre-auth',
  },
  {
    icon: RotateCcw,
    title: 'Refund',
    description: 'Handle full and partial refunds for completed transactions. Includes specifications for immediate refunds and delayed refund processing.',
    link: '/technical-documentation/api-reference/refund',
  },
  {
    icon: Repeat,
    title: 'Recurring Billing',
    description: 'Set up and manage recurring payment schedules for subscriptions, memberships, and regular billing cycles.',
    link: '/technical-documentation/api-reference/recurring-billing',
  },
  {
    icon: Monitor,
    title: 'Hosted Payment Page',
    description: 'Implement secure hosted payment pages that handle the entire payment process whilst maintaining your brand experience.',
    link: '/technical-documentation/api-reference/hosted-payment-page',
    recommended: true,
  },
  {
    icon: Key,
    title: 'Hosted Payment Page (Token)',
    description: 'Advanced hosted payment implementation using tokenisation for returning customers and stored payment methods.',
    link: '/technical-documentation/api-reference/hosted-payment-token',
  },
  {
    icon: Globe,
    title: 'DCC',
    description: 'Dynamic Currency Conversion — enable international customers to pay in their local currency whilst you receive settlement in your preferred currency.',
    link: '/technical-documentation/api-reference/dcc',
  },
  {
    icon: Wallet,
    title: 'Google Pay',
    description: 'Accept Google Pay payments through eCOMM. Server-to-server integration that processes Google Pay tokens for fast, frictionless checkouts.',
    link: '/technical-documentation/api-reference/google-pay',
  },
];

const ApiReference = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col" data-testid="api-reference-page">
      <Header />

      <div className="h-20" />

      {/* Full-page background image */}
      <div className="relative flex-1">
        <div
          className="fixed inset-0 top-20 bg-cover bg-center"
          style={{ backgroundImage: `url(${bgImage})` }}
        />
        <div className="fixed inset-0 top-20 bg-white/0" />

        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
          <button
            onClick={() => navigate('/technical-documentation')}
            className="flex items-center gap-2 text-[#1F4E79] hover:text-[#163a5f] font-medium mb-8 transition-colors duration-200"
            data-testid="api-ref-back-button"
          >
            <ArrowLeft size={18} />
            Back to Technical Documentation
          </button>

          {/* Intro */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6" data-testid="api-ref-title">
              API Reference
            </h1>

            <div className="space-y-4 text-gray-700 text-base sm:text-lg leading-relaxed">
              <p>
                Welcome to our comprehensive API Reference — your definitive guide to integrating with eCOMM Merchant Solutions. This documentation provides detailed specifications, request/response formats and implementation examples for every available API function.
              </p>
            </div>

            <div className="mt-8">
              <h2 className="text-xl font-bold text-gray-900 mb-2">What You'll Find</h2>
              <p className="text-gray-700 text-base leading-relaxed">
                Each API endpoint is thoroughly documented with real-world examples, parameter specifications and expected responses. Whether you're implementing basic payment processing or advanced features like 3D Secure authentication, you'll find the technical details you need to build robust integrations.
              </p>
            </div>

            <div className="mt-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">How to Use This Reference</h2>
              <p className="text-gray-700 text-base leading-relaxed">
                Browse the sections below to find the specific API functions you need. Each section includes complete technical specifications, sample code and implementation notes to ensure successful integration.
              </p>
            </div>
          </div>

          {/* Sandbox credentials request CTA */}
          <SandboxCredentialsCta />

          {/* Topic Cards - 3 per row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {topics.map((topic, index) => {
              const Icon = topic.icon;
              return (
                <Link
                  key={index}
                  to={topic.link}
                  className="group relative bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-6 hover:shadow-xl hover:bg-white/90 transition-all duration-300"
                  data-testid={`api-ref-topic-${index}`}
                >
                  {topic.recommended && (
                    <span
                      className="absolute -top-2.5 -right-2.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white shadow-md bg-gradient-to-r from-[#ef5b25] to-[#f58220]"
                      data-testid="recommended-badge"
                    >
                      <Sparkles size={11} strokeWidth={2.5} />
                      Recommended for new merchants
                    </span>
                  )}
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#1F4E79] transition-colors duration-200 flex items-center gap-2">
                    <Icon size={20} className="text-[#1F4E79] flex-shrink-0" />
                    {topic.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    {topic.description}
                  </p>
                  <span className="inline-flex items-center gap-1 text-[#1F4E79] font-semibold text-sm group-hover:gap-2 transition-all duration-200">
                    View guide <ArrowRight size={16} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      <div className="relative z-10">
        <Footer />
      </div>
      <BackToTop />
    </div>
  );
};

export default ApiReference;
